import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { parseISO } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { DatePicker, Input, Select, TextArea } from "@jhool-io/fe-components";
import { useDebounce } from "../../../../hooks/helpers";
import useToast from "../../../../hooks/useToast";
import { ComboOption } from "../../../../utils/types";
import { IApiResponse } from "../../../../utils/types/api-response";
import { handleFormatDatePickerValue } from "../../../../utils/helpers";
import { useFetchProviders } from "../../../providers/hooks/providers.queries";
import {
    useCreateProviderAccommodation,
    useUpdateProviderAccommodation,
} from "../../hooks/mutations/reports.mutations";
import {
    AccommodationNoteCategory,
    IProviderAccommodation,
} from "../../types/reports.types";
import { getAccommodationNoteCategoryLabel } from "../../helpers/reports.helper";

interface ProviderAccommodationFormProps {
    accommodation?: IProviderAccommodation;
    onFormSubmit: () => void;
}

interface IProviderAccommodationFormValues {
    window_hours: number;
    note_categories: AccommodationNoteCategory[];
    effective_from: Date;
    effective_to: Date | null;
    notes: string;
}

const categoryOptions = [
    { value: "all", label: "All" },
    ...Object.values(AccommodationNoteCategory).map((category) => ({
        value: category,
        label: getAccommodationNoteCategoryLabel(category),
    })),
];

const schema = yup.object({
    window_hours: yup
        .number()
        .typeError("Submission window is required")
        .integer("Submission window must be whole hours")
        .min(25, "Submission window must be more than 24 hours")
        .max(168, "Submission window can't be more than 168 hours")
        .required("Submission window is required"),
    note_categories: yup
        .array()
        .min(1, "Choose at least one note category")
        .required("Choose at least one note category"),
    effective_from: yup
        .date()
        .typeError("Effective from is required")
        .required("Effective from is required"),
    effective_to: yup
        .date()
        .nullable()
        .min(
            yup.ref("effective_from"),
            "Effective to can't be before effective from"
        ),
    notes: yup.string(),
});

export default function ProviderAccommodationForm({
    accommodation,
    onFormSubmit,
}: ProviderAccommodationFormProps) {
    const [providerSearch, setProviderSearch] = React.useState("");
    const [selectedProvider, setSelectedProvider] =
        React.useState<ComboOption | null>(null);
    const debouncedProviderSearch = useDebounce(providerSearch, 500);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<IProviderAccommodationFormValues>({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: accommodation
            ? {
                  window_hours: accommodation.window_hours,
                  note_categories: accommodation.note_categories,
                  effective_from: parseISO(
                      accommodation.effective_from.replace("Z", "")
                  ),
                  effective_to: accommodation.effective_to
                      ? parseISO(accommodation.effective_to.replace("Z", ""))
                      : null,
                  notes: accommodation.notes || "",
              }
            : { note_categories: [], effective_to: null, notes: "" },
    });

    const queryClient = useQueryClient();
    const { toast } = useToast();
    const createAccommodation = useCreateProviderAccommodation();
    const updateAccommodation = useUpdateProviderAccommodation(
        accommodation?.accommodation_id || ""
    );

    const providersQuery = useFetchProviders({
        page: 1,
        limit: 20,
        search_string: debouncedProviderSearch,
    });

    const providerOptions: ComboOption[] =
        providersQuery.data?.data.map((provider) => ({
            label: provider.provider_name,
            value: provider.provider_id,
        })) ?? [];

    const handleSaveSuccess = () => {
        queryClient.invalidateQueries({
            queryKey: ["provider-accommodations"],
        });
        toast({
            mode: "success",
            message: accommodation
                ? "Accommodation updated"
                : "Accommodation added",
        });
        onFormSubmit();
    };

    const handleSaveError = (
        err: AxiosError<IApiResponse<IProviderAccommodation>>
    ) => {
        toast({
            mode: "error",
            message:
                err.response?.data.message ||
                "Could not save the accommodation at this time",
        });
    };

    const onSubmit = (values: IProviderAccommodationFormValues) => {
        if (!accommodation && !selectedProvider) {
            toast({ mode: "error", message: "Provider field cannot be empty" });
            return;
        }
        const payload = {
            window_hours: values.window_hours,
            note_categories: values.note_categories,
            effective_from: handleFormatDatePickerValue(values.effective_from),
            effective_to: values.effective_to
                ? handleFormatDatePickerValue(values.effective_to)
                : null,
            notes: values.notes.trim() || null,
        };
        if (accommodation) {
            updateAccommodation.mutate(payload, {
                onSuccess: handleSaveSuccess,
                onError: handleSaveError,
            });
        } else if (selectedProvider) {
            createAccommodation.mutate(
                { ...payload, provider_id: selectedProvider.value },
                { onSuccess: handleSaveSuccess, onError: handleSaveError }
            );
        }
    };

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            id="provider-accommodation-form"
            className="flex flex-col gap-[--spacing-items]"
        >
            <div className="fg">
                {accommodation ? (
                    <Input
                        label="Provider"
                        value={accommodation.provider_name}
                        disabled
                    />
                ) : (
                    <Select
                        label="Provider"
                        isRequired
                        isSearchable
                        isLongListInDialog
                        isLoading={providersQuery.isLoading}
                        options={providerOptions}
                        value={selectedProvider}
                        onInputChange={(value) => setProviderSearch(value)}
                        filterOption={() => true}
                        onChange={(val) =>
                            setSelectedProvider(val as ComboOption | null)
                        }
                        placeholder={
                            providersQuery.error
                                ? "Error loading providers"
                                : "Select a provider"
                        }
                    />
                )}
            </div>
            <div className="fg">
                <Input
                    {...register("window_hours")}
                    type="number"
                    label="Submission window (hours)"
                    placeholder="48"
                    isRequired
                    infoText="Notes are measured against the window in force on their date of service."
                    hasError={!!errors.window_hours}
                    errorText={errors.window_hours?.message}
                />
            </div>
            <div className="fg">
                <Controller
                    name="note_categories"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Applies to"
                            placeholder="Note categories"
                            options={categoryOptions}
                            value={categoryOptions.filter((option) =>
                                field.value.includes(
                                    option.value as AccommodationNoteCategory
                                )
                            )}
                            onChange={(val) => {
                                const values = (val as ComboOption[]).map(
                                    (option) => option.value
                                );
                                field.onChange(
                                    Object.values(
                                        AccommodationNoteCategory
                                    ).filter(
                                        (category) =>
                                            values.includes("all") ||
                                            values.includes(category)
                                    )
                                );
                            }}
                            multiHasValues={field.value.length > 0}
                            isMulti
                            hideSelectedOptions
                            isLongListInDialog
                            isRequired
                            hasError={!!errors.note_categories}
                            errorText={errors.note_categories?.message}
                        />
                    )}
                />
            </div>
            <div className="fg flex gap-[--spacing-items]">
                <Controller
                    name="effective_from"
                    control={control}
                    render={({ field }) => (
                        <DatePicker
                            label="Effective from"
                            isRequired
                            isInDialog
                            selected={field.value}
                            onChange={(date) => field.onChange(date)}
                            hasError={!!errors.effective_from}
                            errorText={errors.effective_from?.message}
                        />
                    )}
                />
                <Controller
                    name="effective_to"
                    control={control}
                    render={({ field }) => (
                        <DatePicker
                            label="Effective to"
                            isOptional
                            isInDialog
                            selected={field.value}
                            onChange={(date) => field.onChange(date)}
                            hasError={!!errors.effective_to}
                            errorText={errors.effective_to?.message}
                        />
                    )}
                />
            </div>
            <div className="fg">
                <TextArea
                    {...register("notes")}
                    label="Notes"
                    placeholder="Context for the audit team"
                />
            </div>
        </form>
    );
}
